/**
 * UyrukKontrolKarti.jsx — the review of person.person_nationality_id that comes BEFORE the setting "Kişinin uyruğu esas alınsın" is turned on
 * (backlog #430, 2026-10-17; backend GET /personnel/uyruk-kontrol). Sistem Ayarları › Kayıt Ayarları mounts it under the switch.
 *
 * The field was unreviewed data that drove nothing (3,694 foreign / 72,173 T.C. on dev). Once the personal documents follow it, a wrong value changes which documents a worker owes and
 * whether his card can be printed. The card shows how many people carry each nationality and where the field contradicts the person's own numbers:
 *   · YABANCI_TC_NUMARALI  marked foreign, but the id number is a T.C. number: probably a T.C. citizen;
 *   · YERLI_TC_NUMARASIZ   marked T.C., but there is no T.C. number: probably a foreigner, or an unfilled card;
 *   · UYRUK_YOK            no nationality, or a value the catalog does not use: judged by the "any" rules only, never by the foreign set.
 * It tells staff where to look; it does not say which side is right, and nothing here changes a person (the card's own page does). The id numbers are masked.
 *
 * Read only; the backend needs the Kişiler page permission (page.kisi). Without it the card says so instead of showing an error.
 */
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUyrukKontrol, getUyrukKontrolListe } from "../../services/kisiAdminService";

const SAYFA = 25;

function sayi(n) {
  return Number(n ?? 0).toLocaleString();
}

function SinifListesi({ sinif, onKapat }) {
  const { t } = useTranslation("ayarlar");
  const [sayfa, setSayfa] = useState(0);
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setHata(null);
      try {
        const out = await getUyrukKontrolListe(sinif, sayfa, SAYFA);
        if (!ignore) setVeri(out);
      } catch (e) {
        if (!ignore) setHata(e.message);
      }
    }
    run();
    return () => { ignore = true; };
  }, [sinif, sayfa]);

  const toplam = Number(veri?.toplam ?? 0);
  const sayfaSayisi = Math.max(1, Math.ceil(toplam / SAYFA));
  return (
    <Modal isOpen toggle={onKapat} size="lg" scrollable>
      <ModalHeader toggle={onKapat}>{t(`uyrukKontrol.siniflar.${sinif}.ad`)}</ModalHeader>
      <ModalBody>
        <p className="small text-muted">{t(`uyrukKontrol.siniflar.${sinif}.aciklama`)}</p>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        {veri == null && !hata && <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>}
        {veri && (
          <div className="table-responsive">
            <Table size="sm" className="align-middle small mb-0">
              <thead className="table-light">
                <tr><th>{t("uyrukKontrol.kisi")}</th><th>{t("uyrukKontrol.kimlikNo")}</th><th>{t("uyrukKontrol.pasaportNo")}</th><th>{t("uyrukKontrol.uyruk")}</th></tr>
              </thead>
              <tbody>
                {(veri.kayitlar ?? []).length === 0 && <tr><td colSpan={4} className="text-center text-muted py-3">{t("uyrukKontrol.kimseYok")}</td></tr>}
                {(veri.kayitlar ?? []).map((k) => (
                  <tr key={k.personId} data-testid="uyruk-kisi">
                    <td>
                      {/* the card's own page is where the field is corrected */}
                      <Link to={`/Management/Kisi/Personeller/${k.personId}`}>{[k.ad, k.soyad].filter(Boolean).join(" ") || `#${k.personId}`}</Link>
                      <span className="text-muted ms-1">#{k.personId}</span>
                    </td>
                    <td className="text-nowrap">{k.kimlikNoMaskeli ?? "—"}</td>
                    <td className="text-nowrap">{k.pasaportNo ?? "—"}</td>
                    <td>{t(`uyrukKontrol.uyruklar.${[1, 2].includes(Number(k.uyrukId)) ? Number(k.uyrukId) : 0}`)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <div className="small text-muted">{t("uyrukKontrol.sayfa", { sayfa: sayfa + 1, toplam: sayfaSayisi })}</div>
        <div className="d-flex gap-2">
          <Button size="sm" color="light" disabled={sayfa === 0} onClick={() => setSayfa((s) => s - 1)}>{t("uyrukKontrol.onceki")}</Button>
          <Button size="sm" color="light" disabled={sayfa + 1 >= sayfaSayisi} onClick={() => setSayfa((s) => s + 1)}>{t("uyrukKontrol.sonraki")}</Button>
          <Button size="sm" color="secondary" onClick={onKapat}>{t("uyrukKontrol.kapat")}</Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default function UyrukKontrolKarti() {
  const { t } = useTranslation("ayarlar");
  const [ozet, setOzet] = useState(null);
  const [hata, setHata] = useState(null);
  const [yetkisiz, setYetkisiz] = useState(false);
  const [acik, setAcik] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const out = await getUyrukKontrol();
        if (!ignore) setOzet(out);
      } catch (e) {
        if (ignore) return;
        if (e.status === 403) setYetkisiz(true);
        else setHata(e.message);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  return (
    <Card className="mb-3" data-testid="uyruk-kontrol">
      <CardHeader className="py-2 fw-semibold">{t("uyrukKontrol.baslik")}</CardHeader>
      <CardBody>
        <p className="text-muted small">{t("uyrukKontrol.aciklama")}</p>
        {yetkisiz && <Alert color="warning" className="small py-2" data-testid="uyruk-yetkisiz">{t("uyrukKontrol.yetkiGerekir")}</Alert>}
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        {!ozet && !hata && !yetkisiz && <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>}
        {ozet && (
          <>
            <div className="d-flex flex-wrap gap-3 mb-3" data-testid="uyruk-sayilar">
              <div><Badge color="light" className="text-body border fs-6">{sayi(ozet.yerli)}</Badge> <span className="small">{t("uyrukKontrol.yerli")}</span></div>
              <div><Badge color="light" className="text-body border fs-6">{sayi(ozet.yabanci)}</Badge> <span className="small">{t("uyrukKontrol.yabanci")}</span></div>
              <div><Badge color="light" className="text-body border fs-6">{sayi(ozet.bilinmeyen)}</Badge> <span className="small">{t("uyrukKontrol.bilinmeyen")}</span></div>
              <div className="ms-auto small">
                {t("uyrukKontrol.anahtarDurumu")}{" "}
                <Badge color={ozet.uyrukEsasi ? "warning" : "secondary"} data-testid="uyruk-anahtar">{ozet.uyrukEsasi ? t("uyrukKontrol.acik") : t("uyrukKontrol.kapali")}</Badge>
              </div>
            </div>
            <Table size="sm" className="align-middle mb-2">
              <thead className="table-light"><tr><th>{t("uyrukKontrol.sinif")}</th><th className="text-end">{t("uyrukKontrol.adet")}</th><th /></tr></thead>
              <tbody>
                {(ozet.siniflar ?? []).map((s) => (
                  <tr key={s.sinif} data-testid="uyruk-sinif">
                    <td className="small">{t(`uyrukKontrol.siniflar.${s.sinif}.ad`)}<div className="text-muted" style={{ fontSize: 11 }}>{t(`uyrukKontrol.siniflar.${s.sinif}.aciklama`)}</div></td>
                    <td className="text-end fw-semibold">{sayi(s.adet)}</td>
                    <td className="text-end">
                      <Button size="sm" color="light" disabled={Number(s.adet) === 0} onClick={() => setAcik(s.sinif)} data-testid={`uyruk-listele-${s.sinif}`}>{t("uyrukKontrol.listele")}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="small text-muted">{t("uyrukKontrol.nasilKullanilir", { tutarli: sayi(ozet.tutarli) })}</div>
          </>
        )}
      </CardBody>
      {acik && <SinifListesi sinif={acik} onKapat={() => setAcik(null)} />}
    </Card>
  );
}
